import { useCallback, useContext, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { max } from 'date-fns';
import spacetime from 'spacetime';
import { v4 as uuidv4 } from 'uuid';
import { FormSpy } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';

import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IAgreementChangeReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IAgreementChangeReasonSelectOption';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IErknmFormAgreementChange } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreementChange';
import { lodashGet } from 'functions/lodashGet';
import { agreementsChangeTypeSelectOptions } from 'KnmPackage/MainModule/constants/agreementsChangeTypeSelectOptions';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { getKnmErknmPositionsSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionPositionsSelectOptions } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { IAgreementChangeType } from 'APIPackage/CatalogsModule/interfaces/IAgreementChangeType';

import { checkHasErrorInCollapseBlock } from '../../../../functions/checkHasErrorInCollapseBlock';
import { IKnmFormRef } from '../../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from '../../../../UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from '../../../../UIPackage/MainModule/components/SelectInputField';
import { IRootState } from '../../../../CorePackage/MainModule/interfaces/IRootState';
import { geCatalogsAgreementChangeReasonSelectOptions } from '../../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { Row } from '../../../../UIPackage/FormModule/components/Row';
import { Button } from '../../../../UIPackage/MainModule/components/Button';
import { getTitleFieldWithRedStar } from '../../../../functions/getTitleFieldWithRedStar';
import { AgreementsContext } from '../../AgreementsContext';
import styles from './AgreementChangeItem.module.scss';
import { AgreementsSupportingDocumentsAttachment } from '../AgreementsSupportingDocuments/AgreementsSupportingDocumentsAttachment';

interface IErknmAgreementsItemProps {
  id: string;
  index: number;
  onRemove: () => void;
  name: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  value: IErknmFormAgreementChange;
  watchOnlyMain: boolean;
}

interface IAgreementChangeProps {
  agreementChangeReasons: IAgreementChangeReasonSelectOption[];
  positionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  decisionPositionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
}

export const AgreementChangeItemComponent = ({
  id,
  onRemove,
  index,
  name,
  formRef,
  value,
  agreementChangeReasons,
  decisionPositionsSelectOptions,
  positionsSelectOptions,
  watchOnlyMain,
}: IErknmAgreementsItemProps & IAgreementChangeProps) => {
  const { isKnm, isPm, isDecision, agreement } = useContext(AgreementsContext);
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  /**
   * блокиреутся вся форма, если изменение было подписано
   */
  const isWatchOnly = !!value.signed || watchOnlyMain;

  const agreementChangeReasonList = useMemo(() => {
    if (!agreementChangeReasons || !value.types || value.types.length === 0) {
      return [];
    }

    return agreementChangeReasons
      .filter(el => value.types?.map(i => i?.type?.value).includes(el.code))
      .filter(reason => !value.reasons?.find(i => i?.id === reason.id));
  }, [value, agreementChangeReasons]);

  const unusedAgreementsChangeTypeSelectOptions = useMemo(() => {
    return agreementsChangeTypeSelectOptions.filter(type => !value.types?.find(i => i?.type?.id === type.id));
  }, [value]);

  const addType = useCallback(() => {
    if (!isWatchOnly && push) {
      push(`${name}.types`, { guid: uuidv4(), type: undefined });
    }
  }, [formRef, push, isWatchOnly]);

  const addReason = useCallback(() => {
    if (!isWatchOnly && push) {
      push(`${name}.reasons`, undefined);
    }
  }, [formRef, push, isWatchOnly]);

  /**
   * Удаление элемента
   */
  const makeRemoveAgreementChangesInnerArrayHandler = (fieldName: string, fieldIndex: number) => () => {
    remove && remove(fieldName, fieldIndex);
  };

  const minTermOfExecutionAgreement = useMemo(() => {
    const termOfExecutionAgreement =
      parseDate({
        date: agreement?.termOfExecutionAgreement || '',
        parseFormat: DATE_FORMAT_LONG_FNS,
      }) || new Date();

    if (!agreement?.changes) return max([new Date(), termOfExecutionAgreement]);

    const otherTermOfExecutionAgreements = agreement.changes
      .filter(i => i.guid !== value.guid)
      .map(i =>
        parseDate({
          date: i.termOfExecutionAgreement || '',
          parseFormat: DATE_FORMAT_LONG_FNS,
        }),
      ) as Date[];

    if (!otherTermOfExecutionAgreements.length) {
      return max([new Date(), termOfExecutionAgreement]);
    }

    return max(otherTermOfExecutionAgreements);
  }, [agreement, value]);

  return (
    <KnmCollapse
      theme="fields"
      title={`Изменение №${index + 1}`}
      id={id}
      opened={true}
      onClose={onRemove}
      changeOpenedCallback={setIsKnmCollapseOpened}
      error={isKnmCollapseOpened && hasError}
    >
      <>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: 'Вид изменения',
            field: `${name}.types`,
            isKnm,
            isPm,
            isDecision,
          })}
          id={`${id}.types`}
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={isWatchOnly}
              onClick={addType}
            />
          }
        >
          <div className={styles.ReasonsWrapper}>
            <FieldArray name={`${name}.types`} validate={makeRequired(REQUIRED_ERROR)}>
              {({ fields }: FieldArrayRenderProps<IAgreementChangeType, HTMLElement>) => {
                if (!fields.length) {
                  return null;
                }

                return fields.map((typeName, typeIndex) => {
                  const fieldValue: IAgreementChangeType | undefined = fields.value?.[typeIndex];
                  const itemArrayId = `${id}.types[${fieldValue?.guid}].type`;

                  return (
                    <Row key={`types.${typeIndex}.${fieldValue?.guid}`} margin="none" isListItem>
                      <div className={`shared-field-row ${styles.TerminationReasonsRow}`} id={itemArrayId}>
                        <SelectInputField
                          name={`${typeName}.type`}
                          placeholder={'Вид'}
                          size="medium"
                          options={unusedAgreementsChangeTypeSelectOptions}
                          disabled={isWatchOnly}
                        />
                        {!isWatchOnly && (
                          <CloseButton
                            className="shared-deleting-row-btn"
                            onClick={makeRemoveAgreementChangesInnerArrayHandler(`${name}.types`, typeIndex)}
                          />
                        )}
                      </div>
                    </Row>
                  );
                });
              }}
            </FieldArray>
          </div>
          <FormSpy subscription={{ errors: true, touched: true }}>
            {props => {
              const error = lodashGet(formRef?.current.getState()?.errors || {}, `${name}.types`);

              if (error) {
                const errorMessage: string | undefined =
                  error && typeof error === 'string'
                    ? (Object.values(error).reduce((prev: string, curr: string) => prev + curr, '') as string)
                    : undefined;

                if (errorMessage && props.touched?.[`${name}.types`]) {
                  return <p className="shared-input-error">Поле не может быть пустым</p>;
                }
              }

              return null;
            }}
          </FormSpy>
        </FieldBlock>

        <FieldBlock
          margin="top"
          id={`${id}.reasons`}
          label={getTitleFieldWithRedStar({
            label: 'Основание для изменения',
            field: `${name}.reasons`,
            isKnm,
            isPm,
            isDecision,
          })}
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              onClick={addReason}
              disabled={isWatchOnly}
            />
          }
        >
          <div className={styles.ReasonsWrapper}>
            <FieldArray name={`${name}.reasons`} validate={makeRequired(REQUIRED_ERROR)}>
              {({ fields }: FieldArrayRenderProps<IAgreementChangeReasonSelectOption, HTMLElement>) => {
                if (!fields.length) {
                  return null;
                }

                return fields.map((reasonsName, reasonIndex) => {
                  const fieldValue: IAgreementChangeReasonSelectOption | undefined = fields.value?.[reasonIndex];
                  const itemArrayId = `${id}.reasons[${reasonIndex}]`;

                  return (
                    <Row key={`reasons.${reasonIndex}.${fieldValue?.id}`} margin="none" isListItem>
                      <div className={`shared-field-row ${styles.TerminationReasonsRow}`} id={itemArrayId}>
                        <SelectInputField
                          name={reasonsName}
                          placeholder={'Основание'}
                          size="medium"
                          options={agreementChangeReasonList}
                          disabled={isWatchOnly}
                        />
                        {!isWatchOnly && (
                          <CloseButton
                            className="shared-deleting-row-btn"
                            onClick={makeRemoveAgreementChangesInnerArrayHandler(`${name}.reasons`, reasonIndex)}
                          />
                        )}
                      </div>
                    </Row>
                  );
                });
              }}
            </FieldArray>
          </div>
          <FormSpy subscription={{ errors: true, touched: true }}>
            {props => {
              const error = lodashGet(formRef?.current.getState()?.errors || {}, `${name}.reasons`);

              if (error) {
                const errorMessage: string | undefined =
                  error && typeof error === 'string'
                    ? (Object.values(error).reduce((prev: string, curr: string) => prev + curr, '') as string)
                    : undefined;

                if (errorMessage && props.touched?.[`${name}.reasons`]) {
                  return <p className="shared-input-error">Поле не может быть пустым</p>;
                }
              }

              return null;
            }}
          </FormSpy>
        </FieldBlock>

        <AgreementsSupportingDocumentsAttachment
          id={`${id}.petitionAttachment`}
          watchOnly={isWatchOnly}
          isRequired={true}
          name={`${name}.petitionAttachment`}
          hideButtonAdd={!!value.attachment && value.attachment.length > 0}
          formRef={formRef}
          title={getTitleFieldWithRedStar({
            label: 'Ходатайство об изменении соглашения',
            field: `${name}.petitionAttachment`,
            isKnm,
            isPm,
            isDecision,
          })}
          acceptedFiles={'.pdf'}
          type="one"
        />

        {value.types?.find(type => type.type?.value === 'EXTENSION_OF_DEADLINE') && (
          <FieldBlock
            placementLabel="left"
            color="black"
            className={styles.FieldBlock}
            id={`${id}.termOfExecutionAgreement`}
            label={getTitleFieldWithRedStar({
              label: 'Новый срок исполнения',
              field: `${name}.termOfExecutionAgreement`,
              isKnm,
              isPm,
              isDecision,
            })}
          >
            <div>
              <DatePickerField
                name={`${name}.termOfExecutionAgreement`}
                placeholder={'Новый срок исполнения'}
                size="medium"
                minDate={minTermOfExecutionAgreement}
                maxDate={spacetime().add(3, 'years').toNativeDate()}
                className={styles.DatePicker}
                disabled={isWatchOnly}
              />
            </div>
          </FieldBlock>
        )}

        <AgreementsSupportingDocumentsAttachment
          id={`${id}.updatedAgreementAttachment`}
          watchOnly={isWatchOnly}
          isRequired={true}
          name={`${name}.updatedAgreementAttachment`}
          hideButtonAdd={!!value.updatedAgreementAttachment && value.updatedAgreementAttachment.length > 0}
          formRef={formRef}
          title={getTitleFieldWithRedStar({
            label: 'Обновленная версия соглашения',
            field: `${name}.updatedAgreementAttachment`,
            isKnm,
            isPm,
            isDecision,
          })}
          acceptedFiles={'.pdf'}
          type="one"
        />

        <AgreementsSupportingDocumentsAttachment
          id={`${id}.attachment`}
          watchOnly={isWatchOnly}
          isRequired={true}
          name={`${name}.attachment`}
          hideButtonAdd={!!value.attachment && value.attachment.length > 0}
          formRef={formRef}
          title={getTitleFieldWithRedStar({
            label: 'Решение об изменении',
            field: `${name}.attachment`,
            isKnm,
            isPm,
            isDecision,
          })}
          acceptedFiles={'.pdf'}
          type="one"
        />

        <FieldBlock label={'Решение принято'} margin="top">
          <FieldBlock
            id={`${id}.fioSigner`}
            placementLabel="left"
            color="black"
            className={styles.FieldBlock}
            label={getTitleFieldWithRedStar({
              label: 'ФИО принявшего решение об изменении',
              field: `${name}.fioSigner`,
              isKnm,
              isPm,
              isDecision,
            })}
          >
            <TextInputField name={`${name}.fioSigner`} placeholder="ФИО" disabled={isWatchOnly} />
          </FieldBlock>
          <FieldBlock
            id={`${id}.titleSigner`}
            placementLabel="left"
            color="black"
            className={styles.FieldBlock}
            label={getTitleFieldWithRedStar({
              label: 'Должность принявшего решение об изменении',
              field: `${name}.titleSigner`,
              isKnm,
              isPm,
              isDecision,
            })}
          >
            <SelectInputField
              name={`${name}.titleSigner`}
              placeholder="Выберите должность"
              options={isDecision ? decisionPositionsSelectOptions : positionsSelectOptions}
              isSearchable
              size="medium"
              opensOnTop
              disabled={isWatchOnly}
            />
          </FieldBlock>
        </FieldBlock>
      </>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IAgreementChangeProps => ({
  agreementChangeReasons: geCatalogsAgreementChangeReasonSelectOptions(state),
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  decisionPositionsSelectOptions: getDecisionPositionsSelectOptions(state),
});

export const AgreementChangeItem = connect(mapStateToProps)(AgreementChangeItemComponent);
